/**
 * AGM Exempted UFS — 문서별 signers / Signers JSON.
 */
import { dedupePresetSigners } from '../../../presetSignerDedup';
import type { PresetDocCustSigner } from '../../../presetDocCustSave';
import type { AgmEntityPersonRow, AgmExemptedUfsFormModel } from './useFormPayload';
import type { AgmExemptedUfsDocKind } from './agmExemptedUfsDocVariableMap';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function toSignersJsonPayload(list: PresetDocCustSigner[]): { signers: string; Signers: string } | null {
  if (!list.length) return null;
  const json = JSON.stringify(list);
  return { signers: json, Signers: json };
}

function dedupKey(row: AgmEntityPersonRow): string {
  const cd = trim(row.contactCd);
  if (cd) return `cd:${cd.toUpperCase()}`;
  const reg = trim(row.contactRegNo || '').replace(/[\s-]/g, '').toUpperCase();
  if (reg) return `reg:${reg}`;
  return `name:${trim(row.name).toLowerCase()}`;
}

function appendRowIfNew(rows: AgmEntityPersonRow[], row: AgmEntityPersonRow): void {
  const name = trim(row.name);
  if (!name) return;
  const key = dedupKey({ ...row, name });
  if (rows.some((r) => dedupKey(r) === key)) return;
  rows.push({ ...row, name });
}

/** Members' Resolutions — 주주(멤버) 서명 대상 수집 */
export function collectAgmExemptedMemberSignerRows(m: AgmExemptedUfsFormModel): AgmEntityPersonRow[] {
  const rows: AgmEntityPersonRow[] = [];

  if (m.memberType === 'multiple') {
    for (const mem of m.members || []) {
      appendRowIfNew(rows, {
        name: trim(mem.name),
        contactCd: trim(mem.contactCd),
        role: 'shareholder',
      });
    }
  }

  if (m.memberType === 'multiple') {
    for (const mem of m.entityMembers || []) {
      appendRowIfNew(rows, { ...mem, role: 'shareholder' });
    }
  }

  return rows;
}

/** Sole Corporate — corporate member_name 1명만 (대표명·entityMembers 제외) */
function collectSoleCorporateMemberSignerRow(m: AgmExemptedUfsFormModel): AgmEntityPersonRow[] {
  const name = trim(m.memberName);
  if (!name) return [];
  return [{ name, role: 'shareholder' }];
}

/** Sole Individual — member_name 1명만 */
function collectSoleIndividualMemberSignerRow(m: AgmExemptedUfsFormModel): AgmEntityPersonRow[] {
  const name = trim(m.memberName);
  if (!name) return [];
  return [{ name, role: 'shareholder' }];
}

function buildMemberResolutionSignerJson(
  rows: AgmEntityPersonRow[],
): { signers: string; Signers: string } | null {
  const list = dedupePresetSigners(
    rows
      .map((r) => ({
        name: trim(r.name),
        email: trim(r.email || ''),
        role: 'shareholder' as const,
        contactCd: trim(r.contactCd),
        contactRegNo: trim(r.contactRegNo),
      }))
      .filter((s) => s.name),
    { defaultRole: 'shareholder' },
  );
  return toSignersJsonPayload(list);
}

/** Directors' Resolutions — 서명 대상 행 (3인 이상 수동 지정 시 form.signers, 아니면 entityDirectors) */
function collectAgmExemptedUfsDirectorsResolutionSignerRows(
  m: AgmExemptedUfsFormModel,
): PresetDocCustSigner[] {
  let rows: PresetDocCustSigner[];

  if (m.specifySigners && m.directorCount >= 3) {
    rows = (m.signers || [])
      .map((s) => ({ name: trim(s.name), email: '', role: 'director' as const }))
      .filter((s) => s.name);
  } else {
    rows = (m.entityDirectors || [])
      .map((d) => ({
        name: trim(d.name),
        email: trim(d.email || ''),
        role: 'director' as const,
        contactCd: trim(d.contactCd),
        contactRegNo: trim(d.contactRegNo),
      }))
      .filter((s) => s.name);
    if (!rows.length) {
      rows = (m.signers || [])
        .map((s) => ({ name: trim(s.name), email: '', role: 'director' as const }))
        .filter((s) => s.name);
    }
  }

  return dedupePresetSigners(rows);
}

/**
 * Directors' Resolutions — [[designated_signers]]/[[signer_name]] + [[Signers]] 공통 payload.
 */
export function buildAgmExemptedUfsDirectorsResolutionDocumentVariables(
  m: AgmExemptedUfsFormModel,
): Record<string, string | Array<Record<string, string>>> {
  const deduped = collectAgmExemptedUfsDirectorsResolutionSignerRows(m);
  const signers_list = deduped.map((r) => ({ signer_name: trim(r.name) }));
  const payload: Record<string, string | Array<Record<string, string>>> = {
    designated_signers: signers_list,
    signers_list,
  };
  const jsonPayload = toSignersJsonPayload(deduped);
  if (jsonPayload) {
    payload.signers = jsonPayload.signers;
    payload.Signers = jsonPayload.Signers;
  }
  return payload;
}

/** @deprecated Prefer buildAgmExemptedUfsDirectorsResolutionDocumentVariables */
export function buildAgmExemptedUfsDirectorsResolutionSignerVariables(
  m: AgmExemptedUfsFormModel,
): { signers: string; Signers: string } | null {
  const docVars = buildAgmExemptedUfsDirectorsResolutionDocumentVariables(m);
  if (!docVars.signers || !docVars.Signers) return null;
  return { signers: String(docVars.signers), Signers: String(docVars.Signers) };
}

/** Members' Resolutions (Multiple) — 주주 전원 */
export function buildAgmExemptedUfsMembersResolutionMultipleSignerVariables(
  m: AgmExemptedUfsFormModel,
): { signers: string; Signers: string } | null {
  return buildMemberResolutionSignerJson(collectAgmExemptedMemberSignerRows(m));
}

/** Members' Resolutions (Sole Individual) — member_name 1명 */
export function buildAgmExemptedUfsMembersResolutionSoleIndividualSignerVariables(
  m: AgmExemptedUfsFormModel,
): { signers: string; Signers: string } | null {
  return buildMemberResolutionSignerJson(collectSoleIndividualMemberSignerRow(m));
}

/** Members' Resolutions (Sole Corporate) — corporate member_name 1명만 */
export function buildAgmExemptedUfsMembersResolutionSoleCorporateSignerVariables(
  m: AgmExemptedUfsFormModel,
): { signers: string; Signers: string } | null {
  return buildMemberResolutionSignerJson(collectSoleCorporateMemberSignerRow(m));
}

export function buildAgmExemptedUfsMembersResolutionSignerVariables(
  m: AgmExemptedUfsFormModel,
  docKind: AgmExemptedUfsDocKind,
): { signers: string; Signers: string } | null {
  if (docKind === 'members_resolution_multiple') {
    return buildAgmExemptedUfsMembersResolutionMultipleSignerVariables(m);
  }
  if (docKind === 'members_resolution_sole_corporate') {
    return buildAgmExemptedUfsMembersResolutionSoleCorporateSignerVariables(m);
  }
  if (docKind === 'members_resolution_sole_individual') {
    return buildAgmExemptedUfsMembersResolutionSoleIndividualSignerVariables(m);
  }
  return null;
}
